import Color from 'color';
import { isEqual } from 'lodash';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FormTheme } from 'api/form/types';
import { ColorPicker } from 'components/molecules/color-picker/color-picker';
import { Label01 } from 'components/styles';
import { useAccount } from 'hooks/use-account';
import { useFormTemplateUpdateMutation } from 'queries/form-template';
import { DEFAULT_CUSTOM_COLOR_CODE, FormThemesList } from './constants';
import { ThemeIconRenderer } from './theme-components';

interface ColorThemesProps {
  formTheme: FormTheme;
  templateId: number;
}
const Wrapper = styled.div`
  padding: var(--layout-02) var(--layout-01);
  border-bottom: 1px solid var(--border-color);
`;
const ThemeListWrapper = styled.div`
  margin-top: var(--spacing-04);
  display: flex;
  flex-wrap: wrap;
`;

const CustomThemeWrapper = styled.div`
  margin-top: var(--spacing-06);
`;
const CustomThemeColor = styled.div`
  display: flex;
  align-items: center;
  margin-top: var(--spacing-05);
`;
const ColorPickerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: var(--spacing-05);
`;
const ColorPickerLabel = styled.div`
  ${Label01};
  margin-bottom: var(--spacing-03);
`;

export const computeThemeMapping = (customColor: string): FormTheme => {
  return {
    backgroundColor: Color(customColor).mix(Color('#fff'), 0.9).hex(),
    titleBarColor: customColor,
    headerColor: Color(customColor).darken(0.5).hex(),
    buttonColor: customColor,
  };
};

const CustomTheme = ({
  formTheme,
  isSelected,
  setThemeChange,
}: {
  formTheme: FormTheme;
  isSelected: boolean;
  setThemeChange: (updatedFormTheme: FormTheme) => void;
}) => {
  const {
    metaData: { theme },
  } = useAccount();
  const [customColor, setCustomColor] = useState(
    isSelected
      ? formTheme.titleBarColor
      : theme?.brandColor ?? DEFAULT_CUSTOM_COLOR_CODE,
  );

  const customFormTheme = useMemo(() => {
    return computeThemeMapping(customColor);
  }, [customColor]);

  const onColorChange = (value) => {
    if (value) {
      setCustomColor(value);
      setThemeChange(computeThemeMapping(value));
    }
  };

  return (
    <CustomThemeWrapper>
      <FormattedMessage id="picker.custom" />
      <CustomThemeColor>
        <ThemeIconRenderer
          themeColor={customFormTheme}
          isSelected={isSelected}
          setThemeChange={setThemeChange}
        />
        <ColorPickerWrapper>
          <ColorPickerLabel>
            <FormattedMessage id="form_templates.brand_color" />
          </ColorPickerLabel>

          <ColorPicker
            placement="top"
            hideColorCode
            value={customColor}
            onChange={onColorChange}
          />
        </ColorPickerWrapper>
      </CustomThemeColor>
    </CustomThemeWrapper>
  );
};

export const ColorThemesList: FC<ColorThemesProps> = ({
  formTheme,
  templateId,
}) => {
  const themeColor = {
    backgroundColor: formTheme.backgroundColor,
    titleBarColor: formTheme.buttonColor,
    headerColor: formTheme.headerColor,
    buttonColor: formTheme.buttonColor,
  };
  const isDefaultColorSelected = FormThemesList.find((theme) =>
    isEqual(theme, themeColor),
  );
  const { mutate } = useFormTemplateUpdateMutation(templateId);

  const updateFormThemeColor = (updatedFormTheme: FormTheme) => {
    mutate({
      formTheme: {
        ...formTheme,
        ...updatedFormTheme,
      },
    });
  };

  return (
    <Wrapper>
      <FormattedMessage id="form_templates.themes.color" />
      <ThemeListWrapper>
        {FormThemesList.map((theme) => {
          return (
            <ThemeIconRenderer
              themeColor={theme}
              isSelected={isEqual(theme, themeColor)}
              setThemeChange={updateFormThemeColor}
            />
          );
        })}
      </ThemeListWrapper>
      <CustomTheme
        formTheme={formTheme}
        isSelected={!isDefaultColorSelected}
        setThemeChange={updateFormThemeColor}
      />
    </Wrapper>
  );
};
